import React, { useCallback, useState, FC, useEffect } from 'react';
import { getErrorInField } from 'utils/validation';
import MovablePlaceholder from '../MovablePlaceholder/MovablePlaceholder';
import { ValidationBody } from 'ts/interfaces/Validation';
import withErrorBoundary from 'components/ui/withErrorBoundaries';

interface FormGroupProps {
  onChange: (item: string) => void;
  className?: string;
  value: string;
  label: string;
  validation?: ValidationBody;
  isSubmitted?: boolean;
  disabled?: boolean;
  type?: string;
  allowOnlyNumbers?: boolean;
  movablePlaceholder?: string;
  movablePlaceholderIcon?: any;
  movablePlaceholderSm?: boolean;
  maxLength?: number;
  name?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

const FormGroup: FC<FormGroupProps> = ({
  validation = {},
  isSubmitted = false,
  className = '',
  ...props
}) => {
  const [value, setValue] = useState(props.value);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    setValue(props.value);
  }, [props.value]);

  const isValid = useCallback(() => {
    return getErrorInField(validation, value, isSubmitted);
  }, [value, isSubmitted]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={className}>
      <div
        className={
          'form-group' +
          (isValid() ? ' is-invalid' : '') +
          (props.movablePlaceholder ? ' mb-0' : '') +
          (props.movablePlaceholderSm ? ' movable-placeholder-sm' : '')
        }
      >
        {!props.movablePlaceholder && (
          <label>
            {props.label} {validation?.required && <span className="color-danger">*</span>}
          </label>
        )}

        <input
          className={
            'form-control' +
            (props.movablePlaceholder ? ' movable-placeholder-input' : '') +
            (props.movablePlaceholder && !props.movablePlaceholderIcon
              ? ' movable-placeholder-input-noicon'
              : '')
          }
          name={props.name}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          value={props.value}
          onChange={(event) => {
            const newValue = props.allowOnlyNumbers
              ? event.target.value.replace(/[^\d.]/g, '')
              : event.target.value;
            setValue(newValue);
            props.onChange(newValue);
          }}
          disabled={props.disabled}
          type={props.type}
          maxLength={props.maxLength}
          onKeyDown={props.onKeyDown}
        />
        {props.movablePlaceholder && (
          <MovablePlaceholder
            label={props.movablePlaceholder}
            showSmall={!!(props.value || isFocused)}
            icon={props.movablePlaceholderIcon}
          />
        )}
        {!!isValid() && <div className="form-invalid-text">{isValid()}</div>}
      </div>
    </div>
  );
};

export default withErrorBoundary(FormGroup, 'FormGroup');
